import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Checkbox,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { userProjectService } from '../../services/userProjectService';
import { projectService } from '../../services/projectService';

const ProjectAssignmentDialog = ({ open, onClose, user, onSuccess, onError }) => {
  const [allProjects, setAllProjects] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      loadData();
    }
  }, [open, user]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all projects and user's current projects in parallel
      const [projects, assignments] = await Promise.all([
        projectService.getAll(),
        userProjectService.getUserProjects(user.user_id),
      ]);

      setAllProjects(projects);
      setUserProjects(assignments);

      // Pre-select user's current projects
      const currentProjectCodes = assignments.map((a) => a.project_code);
      setSelectedProjects(currentProjectCodes);
    } catch (err) {
      onError('Failed to load projects: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProject = (projectCode) => {
    setSelectedProjects((prev) => {
      if (prev.includes(projectCode)) {
        return prev.filter((code) => code !== projectCode);
      } else {
        return [...prev, projectCode];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredCodes = getFilteredProjects().map((p) => p.project_code);
    setSelectedProjects(filteredCodes);
  };

  const handleDeselectAll = () => {
    setSelectedProjects([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Replace all project assignments with the selected ones
      const result = await userProjectService.replaceUserProjects(user.user_id, selectedProjects);

      const message = `Successfully updated project assignments for ${user.username}: ${result.added} added, ${result.removed} removed`;
      onSuccess(message);
      onClose(true); // Close with reload
    } catch (err) {
      onError('Failed to update project assignments: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getFilteredProjects = () => {
    if (!searchTerm) return allProjects;

    return allProjects.filter(
      (project) =>
        project.project_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredProjects = getFilteredProjects();
  const initialProjectCodes = userProjects.map((a) => a.project_code);
  const hasChanges =
    JSON.stringify([...selectedProjects].sort()) !== JSON.stringify([...initialProjectCodes].sort());

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, height: '80vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Assign Projects to: {user?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email} • {user?.role}
            </Typography>
          </Box>
          <IconButton onClick={() => onClose(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary */}
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" icon={<CheckCircleIcon />}>
                <strong>{selectedProjects.length}</strong> of <strong>{allProjects.length}</strong>{' '}
                projects selected
                {hasChanges && ' (unsaved changes)'}
              </Alert>
            </Box>

            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search projects by code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 2 }}
            />

            {/* Select/Deselect All */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}>
                Select All {searchTerm && '(Filtered)'}
              </Button>
              <Button size="small" variant="outlined" onClick={handleDeselectAll}>
                Deselect All
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Project List */}
            {filteredProjects.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? 'No projects found matching your search' : 'No projects available'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {filteredProjects.map((project) => {
                  const isSelected = selectedProjects.includes(project.project_code);
                  const wasInitiallyAssigned = initialProjectCodes.includes(project.project_code);

                  return (
                    <ListItem
                      key={project.project_code}
                      button
                      onClick={() => handleToggleProject(project.project_code)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {project.project_code}
                            </Typography>
                            {!wasInitiallyAssigned && isSelected && (
                              <Chip label="NEW" size="small" color="success" />
                            )}
                            {wasInitiallyAssigned && !isSelected && (
                              <Chip label="REMOVING" size="small" color="error" />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              {project.name}
                            </Typography>
                            {project.description && (
                              <Typography
                                variant="caption"
                                component="span"
                                sx={{ display: 'block', color: 'text.secondary' }}
                              >
                                {project.description}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <Chip
                        label={project.status}
                        size="small"
                        color={project.status === 'Active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => onClose(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !hasChanges || loading}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            px: 3,
          }}
        >
          {saving ? 'Saving...' : 'Save Assignments'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectAssignmentDialog;
